import React from "react";
import { axiosget, axiospost } from "../../helpers/httprequest";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import UploadDoc from "./UploadDoc";
import swal from "../../helpers/swal";
import is_null from "../../helpers/is_null";
import { FiBookOpen, FiUpload, FiInfo } from "react-icons/fi";

const ApplicationForm = ({ v, onSubmitStateChange }) => {
  const [entry, setentry] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admissionRequirementforCourse"],
    queryFn: async () => {
      const req = await axiosget(`ms_programme_admission_requirement/${v?.programme?._id}`);
      return req?.data;
    },
  });

  const _apply = async () => {
    try {
      if (is_null(entry)) {
        swal("danger", "Sorry, mode of entry cannot be null");
        return;
      }

      setIsSubmitting(true);
      const req = await axiospost(`ms_applications`, {
        programme: v?.programme?._id,
        payment: v?._id,
        type: entry,
      });
      const { type, message } = req?.data;
      swal(type, message);
    } catch ({ message }) {
      swal("error", message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Notify parent component of button state changes
  React.useEffect(() => {
    if (onSubmitStateChange) {
      const canSubmit = data?.continue === 0;
      onSubmitStateChange({
        canSubmit,
        isSubmitting,
        onSubmit: _apply,
      });
    }
  }, [data?.continue, isSubmitting, entry]);

  return (
    <div className="space-y-6">
      {isLoading && <FormSkeleton fields={3} />}

      {error && (
        <Alert variant="danger">
          <AlertDescription>
            Local server error: {error?.message || error}
          </AlertDescription>
        </Alert>
      )}

      {data?.type !== undefined && (
        <Alert variant="danger">
          <AlertDescription>{data?.message}</AlertDescription>
        </Alert>
      )}

      {/* Mode of Entry Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
          <FiBookOpen className="h-4 w-4 text-primary-600" />
          <span>Select Mode of Entry</span>
        </div>

        <div className="relative">
          <select
            name="examination"
            onChange={(e) => setentry(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-white text-neutral-900"
          >
            <option value="">Select Option</option>
            <option value="JAMB UTME">JAMB UTME</option>
            <option value="Transfer">Transfer</option>
            <option value="Direct Entry">Direct Entry (DE)</option>
          </select>
        </div>
      </div>

      {/* Document Upload Section */}
      {data && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Info Alert */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FiInfo className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  Document Upload Requirements
                </h4>
                <p className="text-sm text-blue-700">
                  Please upload all required documents. Documents marked as optional can be skipped.
                </p>
              </div>
            </div>
          </div>

          {/* Upload Documents Section */}
          {data?.list && data.list.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
                <FiUpload className="h-4 w-4 text-primary-600" />
                <span>Required Documents</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.list?.map((r, i) => (
                  <UploadDoc r={r} key={`expected_upload_${i}`} />
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default ApplicationForm;
